import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Flag as FlagIcon,
  CheckCircle as ResolvedIcon,
  RemoveCircleOutline as DismissIcon,
  Visibility as ViewIcon,
  OpenInNew as OpenIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getAuthHeaders, getApiUrl } from '../utils/apiConfig';
import { ConfirmDialog, InlineMessage, PageContainer, PageHeader, SectionCard, StatusChip } from '../components/ui';
import { useToast } from '../contexts/ToastContext';
import { useParams } from 'react-router-dom';
import { useUrlTab } from '../hooks/useUrlTab';
import { useOrgFeatureFlag } from '../hooks/useOrgFeatureFlags';
import {
  PermalinkNotFoundError,
  usePermalinkNavigation,
  useProductPermalink,
} from '../hooks/useProductPermalink';
import { PermalinkNotFound } from '../components/productPermalink/PermalinkNotFound';
import { PermalinkLoadFailed } from '../components/productPermalink/PermalinkLoadFailed';
import { tokens } from '../theme/tokens';

interface ProductIssue {
  id: string;
  productId: string;
  productSku: string;
  productName: string;
  /** Server-resolved name: title, else SKU, never the raw key (MOT-94 / P19-2). */
  productDisplayName?: string;
  productBrand: string;
  category: string;
  description: string;
  suggestedValue?: string;
  /** 'sync_run' = one-click report filed from a sync run row. */
  source?: 'manual' | 'sync_run';
  issueCode?: string;
  syncRunId?: string;
  integrationName?: string;
  reportedBy: string;
  reporterEmail?: string;
  organizationName?: string;
  status: 'open' | 'in_review' | 'resolved' | 'dismissed';
  resolvedBy?: string;
  resolvedAt?: any;
  resolutionNote?: string;
  agent?: ProductIssueAgent;
  catalogFix?: CatalogFix;
  createdAt: any;
  updatedAt: any;
}

interface ProductIssueAgentPatch {
  path: string;
  before: string | number | boolean | null;
  after: string | number | boolean;
}

interface ProductIssueAgent {
  status: 'running' | 'proposed' | 'failed';
  kind?: 'data' | 'code' | 'needs_human';
  summary?: string;
  dismissed?: boolean;
  patch?: ProductIssueAgentPatch[];
  hypothesis?: string;
  suspectedPaths?: string[];
  prStatus?: 'queued' | 'opened' | 'out_of_scope' | 'needs_ticket';
  prUrl?: string;
  prNote?: string;
}

interface CatalogFixChange {
  field: string;
  before: string | null;
  after: string;
  reason: string;
  fixType: string;
  confidence: number;
}

interface CatalogFix {
  fixType: string;
  confidence: number;
  reason: string;
  changes: CatalogFixChange[];
  state: 'proposed' | 'applied' | 'reverted';
  autoApplied?: boolean;
  appliedBy?: string;
  appliedByEmail?: string;
  appliedAt?: any;
  bulk?: boolean;
}

const CATEGORY_LABELS: Record<string, string> = {
  incorrect_price: 'Incorrect Price',
  incorrect_name: 'Incorrect Name',
  incorrect_description: 'Incorrect Description',
  incorrect_specs: 'Incorrect Specs',
  wrong_image: 'Wrong Image',
  missing_data: 'Missing Data',
  duplicate_product: 'Duplicate',
  discontinued: 'Discontinued',
  other: 'Other',
};

const STATUS_COLORS: Record<string, 'warning' | 'info' | 'success' | 'default'> = {
  open: 'warning',
  in_review: 'info',
  resolved: 'success',
  dismissed: 'default',
};

/** Internal product key such as "promaster_4104" (<brand>_<sku>, no spaces). */
const INTERNAL_KEY_PATTERN = /^([A-Za-z0-9]+)_([A-Za-z0-9][A-Za-z0-9._-]*)$/;

/** SKU for display: the stored SKU, else the SKU embedded in an internal key. */
function issueSku(issue: ProductIssue): string {
  return issue.productSku || INTERNAL_KEY_PATTERN.exec(issue.productId || '')?.[2] || issue.productId;
}

/**
 * Product name for display. Prefers the server-resolved name; for older API
 * responses it still refuses to show the raw internal key and shows the SKU.
 */
function issueProductName(issue: ProductIssue): string {
  if (issue.productDisplayName) return issue.productDisplayName;
  const name = (issue.productName || '').trim();
  if (name && name !== issue.productId && !INTERNAL_KEY_PATTERN.test(name)) return name;
  return issueSku(issue);
}

function formatDate(d: any): string {
  if (!d) return '—';
  const secs = d?.seconds ?? d?._seconds;
  const date = secs ? new Date(secs * 1000) : new Date(d);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Tab label with its count once the server has returned one (0 included). */
function tabLabel(label: string, count: unknown): string {
  return typeof count === 'number' ? `${label} (${count})` : label;
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

const AUTO_ACTOR = 'product-issue-autofix';

function formatProposalValue(value: unknown, emptyLabel: string): string {
  if (value === null || value === undefined || value === '') return emptyLabel;
  return String(value);
}

function agentRowChip(
  agent: ProductIssueAgent | undefined,
  t: Translate,
): { label: string; tone: 'caution' | 'info' | 'problem' } | null {
  if (!agent || agent.dismissed) return null;
  if (agent.status === 'running') return { label: t('productIssueAgent.rowDiagnosing'), tone: 'info' };
  if (agent.status === 'failed') return { label: t('productIssueAgent.rowFailed'), tone: 'problem' };
  if (agent.kind === 'data') return { label: t('productIssueAgent.rowData'), tone: 'caution' };
  if (agent.kind === 'code' && agent.prUrl) return { label: t('productIssueAgent.rowPr'), tone: 'info' };
  if (agent.kind === 'code') return { label: t('productIssueAgent.rowCode'), tone: 'info' };
  if (agent.kind === 'needs_human') return { label: t('productIssueAgent.rowNeeds'), tone: 'caution' };
  return null;
}

function catalogRowChip(
  fix: CatalogFix | undefined,
  t: Translate,
): { label: string; tone: 'info' | 'positive' } | null {
  if (!fix) return null;
  if (fix.state === 'proposed') return { label: t('productIssueAutofix.rowReady'), tone: 'info' };
  if (fix.state === 'applied' && (fix.autoApplied || fix.appliedBy === AUTO_ACTOR)) {
    return { label: t('productIssueAutofix.rowAuto'), tone: 'positive' };
  }
  return null;
}

function ProductIssueAgentBody({
  issue,
  t,
  pending,
  onApply,
  onDismiss,
  suppressPaths,
}: {
  issue: ProductIssue;
  t: Translate;
  pending: boolean;
  onApply: () => void;
  onDismiss: () => void;
  suppressPaths?: string[];
}) {
  const agent = issue.agent;
  if (!agent) return null;
  const open = issue.status === 'open' || issue.status === 'in_review';
  const patches = (agent.patch || []).filter((patch) => !(suppressPaths || []).includes(patch.path));

  if (agent.dismissed) {
    return <Typography variant="body2">{t('productIssueAgent.suggestionDismissed')}</Typography>;
  }
  if (agent.status === 'running') {
    return <Typography variant="body2">{t('productIssueAgent.diagnosing')}</Typography>;
  }
  if (agent.status === 'failed') {
    return <Typography variant="body2">{agent.summary || t('productIssueAgent.failed')}</Typography>;
  }

  const codeNote = agent.kind === 'code'
    ? agent.prUrl
      ? t('productIssueAgent.codeOpened')
      : agent.prStatus === 'out_of_scope'
        ? (agent.prNote || t('productIssueAgent.codeOutOfScope'))
        : agent.prStatus === 'needs_ticket'
          ? t('productIssueAgent.codeNeedsTicket')
          : t('productIssueAgent.codeQueued')
    : agent.kind === 'needs_human'
      ? (agent.summary || t('productIssueAgent.needsPerson'))
      : agent.summary;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {codeNote && <Typography variant="body2">{codeNote}</Typography>}
      {agent.kind === 'code' && agent.hypothesis && agent.hypothesis !== agent.summary && (
        <Typography variant="body2" color="text.secondary">{agent.hypothesis}</Typography>
      )}
      {agent.suspectedPaths && agent.suspectedPaths.length > 0 && (
        <Typography variant="caption" sx={{ fontFamily: tokens.font.mono }}>
          {agent.suspectedPaths.join(', ')}
        </Typography>
      )}
      {agent.prUrl && (
        <Button size="small" startIcon={<OpenIcon />} href={agent.prUrl} target="_blank" rel="noopener noreferrer">
          {t('productIssueAgent.viewPr')}
        </Button>
      )}
      {agent.kind === 'data' && patches.length > 0 && (
        <>
          <InlineMessage severity="info">{t('productIssueAgent.nothingUntilApply')}</InlineMessage>
          {patches.map((patch) => (
            <Box key={patch.path}>
              <Typography variant="caption" sx={{ fontFamily: tokens.font.mono }}>{patch.path}</Typography>
              <Typography variant="body2">
                {t('productIssueAgent.before')}: {formatProposalValue(patch.before, t('productIssueAgent.empty'))}
              </Typography>
              <Typography variant="body2">
                {t('productIssueAgent.after')}: {formatProposalValue(patch.after, t('productIssueAgent.empty'))}
              </Typography>
            </Box>
          ))}
          {open && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" variant="contained" onClick={onApply} disabled={pending}>
                {t('productIssueAgent.apply')}
              </Button>
              <Button size="small" color="inherit" onClick={onDismiss} disabled={pending}>
                {t('productIssueAgent.dismissSuggestion')}
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

function CatalogFixBody({
  fix,
  open,
  pending,
  t,
  onApply,
  onRevert,
}: {
  fix: CatalogFix;
  open: boolean;
  pending: boolean;
  t: Translate;
  onApply: () => void;
  onRevert: () => void;
}) {
  const percent = Math.round((fix.confidence || 0) * 100);
  const fixType = t(`productIssueAutofix.fixType.${fix.fixType}`, { defaultValue: fix.fixType });
  const when = formatDate(fix.appliedAt);
  const automatic = fix.autoApplied || fix.appliedBy === AUTO_ACTOR;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography variant="overline">{fixType}</Typography>
      {fix.reason && <Typography variant="body2">{fix.reason}</Typography>}
      <Typography variant="caption" color="text.secondary">
        {t('productIssueAutofix.confidence', { percent })}
      </Typography>
      {fix.changes.map((change) => (
        <Box key={change.field}>
          <Typography variant="caption" sx={{ fontFamily: tokens.font.mono }}>{change.field}</Typography>
          <Typography variant="body2">
            {t('productIssueAgent.before')}: {formatProposalValue(change.before, t('productIssueAgent.empty'))}
          </Typography>
          <Typography variant="body2">
            {t('productIssueAgent.after')}: {formatProposalValue(change.after, t('productIssueAgent.empty'))}
          </Typography>
        </Box>
      ))}
      {fix.state === 'proposed' && open && (
        <>
          <InlineMessage severity="info">{t('productIssueAutofix.nothingUntilApply')}</InlineMessage>
          <Button size="small" variant="contained" onClick={onApply} disabled={pending}>
            {t('productIssueAutofix.apply')}
          </Button>
        </>
      )}
      {fix.state === 'applied' && (
        <>
          <Typography variant="body2">
            {automatic
              ? t('productIssueAutofix.autoApplied', { date: when })
              : t('productIssueAutofix.appliedBy', { who: fix.appliedByEmail || fix.appliedBy || '', date: when })}
          </Typography>
          <Button size="small" color="inherit" onClick={onRevert} disabled={pending}>
            {t('productIssueAutofix.revert')}
          </Button>
        </>
      )}
    </Box>
  );
}

export default function AdminProductIssuesPage() {
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();
  const { t } = useTranslation() as { t: Translate };
  const { showToast } = useToast();
  const [statusFilter, setStatusFilter] = useUrlTab(
    ['open', 'in_review', 'resolved', 'dismissed'] as const,
    'open',
  );
  const [selectedIssue, setSelectedIssue] = useState<ProductIssue | null>(null);
  const [resolutionNote, setResolutionNote] = useState('');
  // Direct issue links (MOT-833): the open report lives in the address, /app/admin/product-issues/:issueId.
  const { enabled: permalinksOn } = useOrgFeatureFlag('product_permalinks');
  const { issueId: routeIssueId = '' } = useParams<{ issueId: string }>();
  const permalinkNav = usePermalinkNavigation('/app/admin/product-issues', permalinksOn);
  const permalink = useProductPermalink<ProductIssue>({
    enabled: permalinksOn,
    scope: 'issue',
    routeId: routeIssueId,
    fetchById: async (id) => {
      const res = await fetch(`${getApiUrl()}/product-issues/${encodeURIComponent(id)}`, {
        headers: await getAuthHeaders(),
      });
      if (res.status === 404 || res.status === 403) throw new PermalinkNotFoundError();
      if (!res.ok) throw new Error(`Failed to fetch the report (${res.status})`);
      const body = await res.json();
      if (!body?.data) throw new PermalinkNotFoundError();
      return body.data as ProductIssue;
    },
  });
  const selectedIssueRef = useRef<ProductIssue | null>(null);
  selectedIssueRef.current = selectedIssue;
  const previousRouteIssueId = useRef('');
  const previousSelectedIssueId = useRef<string | null>(null);

  // A report opened from the list: it fills the shared entry (the row is the same shape the by-id read returns).
  const openIssue = (issue: ProductIssue, note: string) => {
    setSelectedIssue(issue);
    setResolutionNote(note);
    if (permalinksOn) {
      permalink.seed(issue.id, issue);
      permalinkNav.open(issue.id);
    }
  };

  // The address decides which report is open: a shared link or refresh fills it from the one by-id read,
  // and leaving the address (Back) closes it.
  useEffect(() => {
    if (!permalinksOn) return;
    const previous = previousRouteIssueId.current;
    previousRouteIssueId.current = routeIssueId;
    if (!routeIssueId) {
      if (previous) {
        setSelectedIssue(null);
        setResolutionNote('');
      }
      return;
    }
    const item = permalink.item;
    if (item && item.id === routeIssueId && selectedIssueRef.current?.id !== routeIssueId) {
      setSelectedIssue(item);
      setResolutionNote(item.status === 'open' || item.status === 'in_review' ? '' : item.resolutionNote || '');
    }
  }, [permalinksOn, routeIssueId, permalink.item]);

  // The dialog closing on its own (Close, a resolve, a dismiss) takes the address with it.
  useEffect(() => {
    const previous = previousSelectedIssueId.current;
    previousSelectedIssueId.current = selectedIssue?.id ?? null;
    if (permalinksOn && routeIssueId && previous && !selectedIssue) permalinkNav.close();
  }, [permalinksOn, routeIssueId, selectedIssue, permalinkNav]);
  const [confirmApply, setConfirmApply] = useState(false);
  const [confirmCatalogApply, setConfirmCatalogApply] = useState(false);
  const [confirmRevert, setConfirmRevert] = useState(false);
  const [bulkPreview, setBulkPreview] = useState<{
    count: number;
    truncated: boolean;
    cap: number;
    samples: Array<{ issueId: string; productName: string; productSku: string; field: string; before: string | null; after: string }>;
  } | null>(null);
  const [bulkPreviewLoading, setBulkPreviewLoading] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['product-issues', statusFilter],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const url = `${getApiUrl()}/product-issues?status=${statusFilter}&limit=100`;
      const res = await fetch(url, { headers });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error?.message || body?.message || `Failed to fetch issues (${res.status})`);
      }
      return res.json();
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, status, note }: { id: string; status: string; note?: string }) => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${getApiUrl()}/product-issues/${id}`, {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, resolutionNote: note }),
      });
      if (!res.ok) throw new Error('Failed to update');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-issues'] });
      setSelectedIssue(null);
      setResolutionNote('');
    },
  });

  const agentMutation = useMutation({
    mutationFn: async ({ id, action }: { id: string; action: 'apply' | 'dismiss' }) => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${getApiUrl()}/product-issues/${id}/agent/${action}`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error?.message || body?.message || t('productIssueAgent.applyFailed'));
      }
      return res.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['product-issues'] });
      setConfirmApply(false);
      setSelectedIssue(null);
      showToast(variables.action === 'apply' ? t('productIssueAgent.applied') : t('productIssueAgent.dismissed'), 'success');
    },
    onError: (error) => {
      showToast(error instanceof Error ? error.message : t('productIssueAgent.applyFailed'), 'error');
    },
  });

  const catalogMutation = useMutation({
    mutationFn: async ({ id, action }: { id: string; action: 'apply' | 'revert' }) => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${getApiUrl()}/product-issues/${id}/fix/${action}`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error?.message || body?.message || t(
          action === 'revert' ? 'productIssueAutofix.revertFailed' : 'productIssueAutofix.applyFailed',
        ));
      }
      return res.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['product-issues'] });
      setConfirmCatalogApply(false);
      setConfirmRevert(false);
      setSelectedIssue(null);
      showToast(
        variables.action === 'revert' ? t('productIssueAutofix.reverted') : t('productIssueAutofix.applied'),
        'success',
      );
    },
    onError: (error, variables) => {
      showToast(
        error instanceof Error ? error.message : t(
          variables.action === 'revert' ? 'productIssueAutofix.revertFailed' : 'productIssueAutofix.applyFailed',
        ),
        'error',
      );
    },
  });

  const bulkMutation = useMutation({
    mutationFn: async () => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${getApiUrl()}/product-issues/fixes/apply`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ fixType: 'classification_consistency' }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error?.message || body?.message || t('productIssueAutofix.applyFailed'));
      }
      return res.json() as Promise<{ data: { applied: number; skipped: number; failed: number } }>;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['product-issues'] });
      setBulkPreview(null);
      const counts = result.data || { applied: 0, skipped: 0, failed: 0 };
      showToast(
        counts.failed > 0
          ? t('productIssueAutofix.bulkFailed', counts)
          : t('productIssueAutofix.bulkResult', counts),
        counts.failed > 0 ? 'warning' : 'success',
      );
    },
    onError: (error) => {
      showToast(error instanceof Error ? error.message : t('productIssueAutofix.applyFailed'), 'error');
    },
  });

  const openBulkPreview = async () => {
    setBulkPreviewLoading(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(
        `${getApiUrl()}/product-issues/fixes/preview?fixType=classification_consistency`,
        { headers },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error?.message || body?.message || t('productIssueAutofix.bulkPreviewFailed'));
      }
      const body = await res.json();
      const preview = body?.data;
      if (!preview || !preview.count) {
        showToast(t('productIssueAutofix.bulkEmpty'), 'info');
        return;
      }
      setBulkPreview(preview);
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('productIssueAutofix.bulkPreviewFailed'), 'error');
    } finally {
      setBulkPreviewLoading(false);
    }
  };

  const issues: ProductIssue[] = data?.data || [];
  const agentEnabled = data?.agentEnabled === true;
  const autofixEnabled = data?.autofixEnabled === true;
  const counts = data?.counts || {};

  return (
    <PageContainer maxWidth={1200}>
      <PageHeader
        title="Product Issues"
        description="Data problems reported by customers and sync runs. Triage each report, then resolve it or dismiss it with a note."
        actions={(autofixEnabled || counts.open > 0) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {autofixEnabled && (
              <Button
                size="small"
                variant="outlined"
                onClick={openBulkPreview}
                disabled={bulkPreviewLoading || bulkMutation.isPending}
              >
                {bulkPreviewLoading ? <CircularProgress size={16} /> : t('productIssueAutofix.bulk')}
              </Button>
            )}
            {counts.open > 0 && <Chip label={`${counts.open} open`} color="warning" size="small" />}
          </Box>
        )}
      />

      {permalinksOn && permalink.notFound && !selectedIssue && (
        <PermalinkNotFound what="report" onBack={permalinkNav.close} />
      )}

      {permalinksOn && permalink.loadFailed && !selectedIssue && (
        <PermalinkLoadFailed what="report" onRetry={() => void permalink.refetch()} />
      )}

      <Tabs
        value={statusFilter}
        onChange={(_, v) => setStatusFilter(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: 2 }}
      >
        <Tab value="open" label={tabLabel('Open', counts.open)} />
        <Tab value="in_review" label={tabLabel('In Review', counts.in_review)} />
        <Tab value="resolved" label={tabLabel('Resolved', counts.resolved)} />
        <Tab value="dismissed" label={tabLabel('Dismissed', counts.dismissed)} />
      </Tabs>

      {isLoading && <CircularProgress sx={{ display: 'block', mx: 'auto', mt: 4 }} />}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load issues{error instanceof Error && error.message ? `: ${error.message}` : ''}
        </Alert>
      )}

      {!isLoading && issues.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No {statusFilter.replace('_', ' ')} issues</Typography>
        </Paper>
      )}

      {issues.length > 0 && (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 260 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Reported By</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 120 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {issues.map((issue) => {
                const hideNeedsHuman = autofixEnabled && !!issue.catalogFix && issue.agent?.kind === 'needs_human';
                const agentChip = agentEnabled && !hideNeedsHuman ? agentRowChip(issue.agent, t) : null;
                const catalogChip = autofixEnabled ? catalogRowChip(issue.catalogFix, t) : null;
                return (
                <TableRow key={issue.id} hover>
                  <TableCell sx={{ minWidth: 200 }}>
                    {/* Wrap at spaces only; overflowWrap breaks a word just when it alone overflows the cell. */}
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ maxWidth: 260, wordBreak: 'normal', overflowWrap: 'break-word' }}
                    >
                      {issueProductName(issue)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                      {issueSku(issue)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                      <Chip
                        label={CATEGORY_LABELS[issue.category] || issue.category}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem' }}
                      />
                      {issue.source === 'sync_run' && (
                        <Chip
                          label={issue.issueCode ? `sync · ${issue.issueCode}` : 'sync run'}
                          size="small"
                          color="info"
                          variant="outlined"
                          sx={{ fontSize: '0.65rem' }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ minWidth: 260 }}>
                    <Typography
                      variant="body2"
                      title={issue.description}
                      sx={{
                        maxWidth: 420,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        wordBreak: 'break-word',
                      }}
                    >
                      {issue.description}
                    </Typography>
                    {issue.suggestedValue && (
                      <Typography variant="caption" color="success.main" display="block">
                        Suggested: {issue.suggestedValue}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{issue.reporterEmail || issue.reportedBy}</Typography>
                    {issue.organizationName && (
                      <Typography variant="caption" color="text.secondary" display="block">{issue.organizationName}</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>{formatDate(issue.createdAt)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                      <Chip label={String(issue.status || 'open').replace('_', ' ')} color={STATUS_COLORS[issue.status]} size="small" />
                      {agentChip && (
                        <StatusChip label={agentChip.label} tone={agentChip.tone} />
                      )}
                      {catalogChip && (
                        <StatusChip label={catalogChip.label} tone={catalogChip.tone} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => openIssue(issue, issue.resolutionNote || '')}>
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {issue.status === 'open' && (
                        <Tooltip title="Mark In Review">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => updateMutation.mutate({ id: issue.id, status: 'in_review' })}
                            disabled={updateMutation.isPending}
                          >
                            <FlagIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {(issue.status === 'open' || issue.status === 'in_review') && (
                        <>
                          <Tooltip title="Resolve">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => openIssue(issue, '')}
                              disabled={updateMutation.isPending}
                            >
                              <ResolvedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Dismiss">
                            <IconButton
                              size="small"
                              color="inherit"
                              onClick={() => updateMutation.mutate({ id: issue.id, status: 'dismissed' })}
                              disabled={updateMutation.isPending}
                            >
                              <DismissIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Issue Detail / Resolution Dialog */}
      <Dialog
        open={!!selectedIssue}
        onClose={() => setSelectedIssue(null)}
        maxWidth="sm"
        fullWidth
        fullScreen={fullScreenDialog}
      >
        {selectedIssue && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FlagIcon color="warning" />
              Issue: {issueProductName(selectedIssue)}
            </DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip label={CATEGORY_LABELS[selectedIssue.category] || selectedIssue.category} color="warning" size="small" />
                  <Chip label={String(selectedIssue.status || 'open').replace('_', ' ')} color={STATUS_COLORS[selectedIssue.status]} size="small" />
                  {selectedIssue.productBrand && <Chip label={selectedIssue.productBrand} variant="outlined" size="small" />}
                  <Chip label={issueSku(selectedIssue)} variant="outlined" size="small" sx={{ fontFamily: tokens.font.mono }} />
                </Box>

                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Description</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{selectedIssue.description}</Typography>
                </Paper>

                {selectedIssue.source === 'sync_run' && (
                  <Paper variant="outlined" sx={{ p: 2, borderColor: 'info.light' }}>
                    <Typography variant="subtitle2" color="info.main" gutterBottom>Reported from a sync run</Typography>
                    {selectedIssue.integrationName && (
                      <Typography variant="body2">Integration: {selectedIssue.integrationName}</Typography>
                    )}
                    {selectedIssue.issueCode && (
                      <Typography variant="body2">Code: {selectedIssue.issueCode}</Typography>
                    )}
                    {selectedIssue.syncRunId && (
                      <Typography variant="body2" sx={{ fontFamily: tokens.font.mono, fontSize: '0.8rem' }}>
                        Run: {selectedIssue.syncRunId}
                      </Typography>
                    )}
                  </Paper>
                )}

                {autofixEnabled && selectedIssue.catalogFix && (
                  <SectionCard dense padded title={t('productIssueAgent.suggestedFix')}>
                    <CatalogFixBody
                      fix={selectedIssue.catalogFix}
                      open={selectedIssue.status === 'open' || selectedIssue.status === 'in_review'}
                      pending={catalogMutation.isPending}
                      t={t}
                      onApply={() => setConfirmCatalogApply(true)}
                      onRevert={() => setConfirmRevert(true)}
                    />
                  </SectionCard>
                )}

                {agentEnabled && selectedIssue.agent && !(
                  autofixEnabled && selectedIssue.catalogFix && selectedIssue.agent.kind === 'needs_human'
                ) && (
                  <SectionCard dense padded title={t('productIssueAgent.suggestedFix')}>
                    <ProductIssueAgentBody
                      issue={selectedIssue}
                      t={t}
                      pending={agentMutation.isPending}
                      onApply={() => setConfirmApply(true)}
                      onDismiss={() => agentMutation.mutate({ id: selectedIssue.id, action: 'dismiss' })}
                      suppressPaths={selectedIssue.catalogFix?.changes.map((change) => change.field)}
                    />
                  </SectionCard>
                )}

                {selectedIssue.suggestedValue && (
                  <Paper variant="outlined" sx={{ p: 2, borderColor: 'success.light' }}>
                    <Typography variant="subtitle2" color="success.main" gutterBottom>Suggested Correction</Typography>
                    <Typography variant="body2">{selectedIssue.suggestedValue}</Typography>
                  </Paper>
                )}

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Reported By</Typography>
                    <Typography variant="body2">{selectedIssue.reporterEmail || selectedIssue.reportedBy}</Typography>
                    {selectedIssue.organizationName && (
                      <Typography variant="caption" color="text.secondary">{selectedIssue.organizationName}</Typography>
                    )}
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Reported</Typography>
                    <Typography variant="body2">{formatDate(selectedIssue.createdAt)}</Typography>
                  </Box>
                </Box>

                {(selectedIssue.status === 'open' || selectedIssue.status === 'in_review') && (
                  <TextField
                    label="Resolution Note (optional)"
                    multiline
                    rows={2}
                    fullWidth
                    size="small"
                    value={resolutionNote}
                    onChange={(e) => setResolutionNote(e.target.value)}
                    placeholder="Describe what was fixed or why this was dismissed..."
                  />
                )}

                {selectedIssue.resolutionNote && selectedIssue.status !== 'open' && selectedIssue.status !== 'in_review' && (
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Resolution Note</Typography>
                    <Typography variant="body2">{selectedIssue.resolutionNote}</Typography>
                  </Paper>
                )}
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                size="small"
                startIcon={<OpenIcon />}
                onClick={() => window.open(`/app/products?search=${encodeURIComponent(issueSku(selectedIssue))}`, '_blank')}
              >
                View Product
              </Button>
              <Box sx={{ flex: 1 }} />
              <Button onClick={() => setSelectedIssue(null)}>Close</Button>
              {(selectedIssue.status === 'open' || selectedIssue.status === 'in_review') && (
                <>
                  <Button
                    color="inherit"
                    onClick={() => updateMutation.mutate({ id: selectedIssue.id, status: 'dismissed', note: resolutionNote })}
                    disabled={updateMutation.isPending}
                  >
                    Dismiss
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={() => updateMutation.mutate({ id: selectedIssue.id, status: 'resolved', note: resolutionNote })}
                    disabled={updateMutation.isPending}
                  >
                    {updateMutation.isPending ? 'Saving...' : 'Mark Resolved'}
                  </Button>
                </>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
      <ConfirmDialog
        open={confirmApply && !!selectedIssue}
        title={t('productIssueAgent.confirmTitle')}
        message={t('productIssueAgent.confirmMessage')}
        confirmLabel={t('productIssueAgent.apply')}
        loading={agentMutation.isPending}
        onClose={() => setConfirmApply(false)}
        onConfirm={() => {
          if (selectedIssue) agentMutation.mutate({ id: selectedIssue.id, action: 'apply' });
        }}
      />
      <ConfirmDialog
        open={confirmCatalogApply && !!selectedIssue?.catalogFix}
        title={t('productIssueAutofix.confirmTitle')}
        message={t('productIssueAutofix.confirmMessage')}
        confirmLabel={t('productIssueAutofix.apply')}
        loading={catalogMutation.isPending}
        preview={selectedIssue?.catalogFix && (
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {selectedIssue.catalogFix.changes.map((change) => (
              <Typography key={change.field} variant="body2">
                {change.field}: {formatProposalValue(change.before, t('productIssueAgent.empty'))} → {change.after}
              </Typography>
            ))}
          </Box>
        )}
        onClose={() => setConfirmCatalogApply(false)}
        onConfirm={() => {
          if (selectedIssue) catalogMutation.mutate({ id: selectedIssue.id, action: 'apply' });
        }}
      />
      <ConfirmDialog
        open={confirmRevert && !!selectedIssue?.catalogFix}
        title={t('productIssueAutofix.revertTitle')}
        message={t('productIssueAutofix.revertMessage')}
        confirmLabel={t('productIssueAutofix.revert')}
        confirmColor="warning"
        loading={catalogMutation.isPending}
        onClose={() => setConfirmRevert(false)}
        onConfirm={() => {
          if (selectedIssue) catalogMutation.mutate({ id: selectedIssue.id, action: 'revert' });
        }}
      />
      <ConfirmDialog
        open={!!bulkPreview}
        title={t('productIssueAutofix.bulkTitle', { count: bulkPreview?.count ?? 0 })}
        message={t('productIssueAutofix.bulkMessage')}
        confirmLabel={t('productIssueAutofix.apply')}
        loading={bulkMutation.isPending}
        maxWidth="sm"
        preview={bulkPreview && (
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {bulkPreview.truncated && (
              <Typography variant="caption" color="text.secondary">
                {t('productIssueAutofix.bulkTruncated', { cap: bulkPreview.cap })}
              </Typography>
            )}
            {bulkPreview.samples.map((sample) => (
              <Typography key={sample.issueId} variant="body2">
                {sample.productSku || sample.productName}: {sample.field}{' '}
                {formatProposalValue(sample.before, t('productIssueAgent.empty'))} → {sample.after}
              </Typography>
            ))}
          </Box>
        )}
        onClose={() => { if (!bulkMutation.isPending) setBulkPreview(null); }}
        onConfirm={() => bulkMutation.mutate()}
      />
    </PageContainer>
  );
}
